import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import type { TChangePayload } from '@winnin/ds-date-range-picker'

import { DAYJS_LOCALE_FROM_TOLGEE } from 'consts/languageFromTolgee'
import { useCurrentLanguage, useTranslate } from 'lib/tolgee'
import { TOLGEE_GLOBALS } from 'utils'

import { DD_ACTION_NAMES } from './custom-date-picker-range.consts'
import { CUSTOM_DATE_PICKER_RANGE_I18N } from './custom-date-picker-range.i18n'
import type { ICustomDatePickerRange, TCustomDatePickerRangeViewProps } from './custom-date-picker-range.types'
import {
  createDefaultIsOutsideRange,
  optionsToPresets,
  payloadToOption,
  resolveInitialSelection,
} from './custom-date-picker-range.utils'

/**
 * Maps the product `options` / `selected` / `onChange(option)` contract onto the
 * DS `DateRangePickerDropdown` props (`presets` / `value` / `{ start, end, presetKey }`).
 * Selection, validation, hover preview and the calendar live in the package.
 */
export function useCustomDatePickerRange({
  options,
  selected,
  onChange,
  alignRight = false,
  numberOfMonths = 2,
  isOutsideRange,
  zIndex,
}: Readonly<ICustomDatePickerRange>): TCustomDatePickerRangeViewProps {
  const location = useLocation()
  const t = useTranslate()
  const language = useCurrentLanguage()

  const presets = useMemo(() => optionsToPresets(options, t), [options, t])

  const { value, selectedPresetKey } = useMemo(
    () => resolveInitialSelection(selected, location?.search ?? ''),
    [selected, location?.search]
  )

  const excludeDate = useMemo(() => isOutsideRange ?? createDefaultIsOutsideRange(), [isOutsideRange])

  const labels = useMemo(
    () => ({
      from: t(TOLGEE_GLOBALS.from),
      to: t(TOLGEE_GLOBALS.to),
      cancel: t(TOLGEE_GLOBALS.cancel),
      apply: t(TOLGEE_GLOBALS.apply),
      placeholder: '',
    }),
    [t]
  )

  const triggerLabel = t(CUSTOM_DATE_PICKER_RANGE_I18N.triggerLabel)

  const handleChange = (payload: TChangePayload) => {
    const option = payloadToOption(payload, options)

    if (option) {
      onChange(option)
    }
  }

  return {
    value,
    onChange: handleChange,
    presets,
    selectedPresetKey,
    excludeDate,
    maxDate: new Date(),
    numberOfMonths,
    position: alignRight ? 'bottom-end' : 'bottom-start',
    zIndex,
    locale: DAYJS_LOCALE_FROM_TOLGEE[language] ?? 'en',
    triggerLabel,
    labels,
    slotProps: {
      trigger: { 'data-dd-action-name': DD_ACTION_NAMES.trigger },
      preset: { 'data-dd-action-name': DD_ACTION_NAMES.preset },
      apply: { 'data-dd-action-name': DD_ACTION_NAMES.apply },
      cancel: { 'data-dd-action-name': DD_ACTION_NAMES.cancel },
    },
  }
}
